import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, List } from "lucide-react";
import { ModernHero } from "@/components/ModernHero";
import { TaskListCard } from "@/components/tasks/TaskListCard";
import { AddTaskDropdown } from "@/components/tasks/AddTaskDropdown";
import { useTaskLists, TaskListWithStats } from "@/hooks/useTaskLists";
import { useFamily } from "@/hooks/useFamily";
import { TaskListSkeleton } from "@/components/LoadingStates";
import { NoFamilyMessage } from "@/components/NoFamilyMessage";
import tasksHeroImage from "@/assets/tasks-hero.jpg";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { Badge } from "@/components/ui/badge";
import { useOptimizedTasks } from "@/hooks/useOptimizedTasks";

export default function MobileTasks() {
  const navigate = useNavigate();
  const { family } = useFamily();
  const { taskLists, loading, updateTaskListsOrder, refreshTaskLists } = useTaskLists(family?.id);
  const { tasks, updateTaskList, updateTasksOrder } = useOptimizedTasks(family?.id);
  const { openModal } = useModalNavigation();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    setOverId(over?.id as string || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // Check if we're dragging a task (task IDs are UUIDs)
    const isTaskDrag = tasks.some(t => t.id === activeId);
    
    if (isTaskDrag) {
      // Find the task being dragged
      const draggedTask = tasks.find(t => t.id === activeId);
      if (!draggedTask) return;

      // Check if we're dropping on a list (list IDs or droppable-{listId})
      const targetListId = overId.startsWith('droppable-') ? overId.replace('droppable-', '') : overId;
      const targetList = taskLists.find(l => l.id === targetListId);
      
      if (targetList && draggedTask.list_id !== targetListId) {
        // Moving task to different list
        updateTaskList(activeId, targetListId);
      } else {
        // Reordering within same list - find all tasks in this list
        const listId = draggedTask.list_id;
        const tasksInList = tasks
          .filter(t => t.list_id === listId && t.status === 'pending')
          .map(t => t.id);
        
        const oldIndex = tasksInList.indexOf(activeId);
        const newIndex = tasksInList.indexOf(overId);
        
        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          const reordered = arrayMove(tasksInList, oldIndex, newIndex);
          updateTasksOrder(reordered);
        }
      }
    } else {
      // Dragging a list card
      const oldIndex = taskLists.findIndex(list => list.id === activeId);
      const newIndex = taskLists.findIndex(list => list.id === overId);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedLists = arrayMove(taskLists, oldIndex, newIndex);
        const orderedIds = reorderedLists.map(list => list.id);
        updateTaskListsOrder(orderedIds);
      }
    }
  };

  if (!family && !loading) {
    return (
      <NoFamilyMessage 
        description="Vous devez d'abord créer une famille pour organiser vos listes de tâches"
        buttonText="Créer ma famille"
        variant="tasks"
      />
    );
  }

  if (!family || loading) {
    return (
      <div className="min-h-screen bg-page-gradient">
        <TaskListSkeleton />
      </div>
    );
  }

  // Sort task lists to put event-linked lists at the end
  const sortedTaskLists = [...taskLists].sort((a, b) => (a.event_id ? 1 : 0) - (b.event_id ? 1 : 0));

  // Filter task lists and tasks based on search query
  const filteredTaskLists = (sortedTaskLists || []).map(list => {
    // Search in list name
    const listNameMatch = (list.name || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    // If list name matches or no search query, show all tasks
    if (!searchQuery || listNameMatch) {
      return { ...list, filteredTasks: list.task_previews || [] };
    }
    
    // Otherwise, filter tasks within the list
    const filteredTasks = (list.task_previews || []).filter(task => 
      (task.title || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Only include lists that have matching tasks
    if (filteredTasks.length > 0) {
      return { ...list, filteredTasks };
    }
    
    return null;
  }).filter(Boolean) as (TaskListWithStats & { filteredTasks: any[] })[];

  const totalPendingTasks = (taskLists || []).reduce((sum, list) => sum + (list.pending_count || 0), 0);

  const heroActions = [
    {
      icon: List,
      label: "Créer une liste",
      onClick: () => openModal('createTaskList')
    },
    {
      icon: Plus,
      label: "Ajouter une tâche",
      onClick: () => {
        if (taskLists.length === 1) {
          // Si une seule liste, créer directement avec cette liste
          openModal('createTask', { listId: taskLists[0].id });
        } else if (taskLists.length > 1) {
          // Si plusieurs listes, créer sans listId pour forcer le choix
          openModal('createTask');
        }
      }
    }
  ];

  return (
    <div className="min-h-screen bg-page-gradient">
      <ModernHero
        title="To-do list"
        actions={heroActions}
        backgroundImage={tasksHeroImage}
        category="tasks"
        allowImageChange={true}
        height="40vh"
      />

      {/* Content Section */}
      <div className="relative -mt-24 bg-content-warm rounded-t-3xl pt-8 px-4 min-h-fit pb-8 z-20">
        {/* Search */}
        <Input
          type="text"
          placeholder="Rechercher dans les listes et tâches..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full mb-6"
        />

        {/* Task Lists */}
        {loading ? (
          <TaskListSkeleton />
        ) : filteredTaskLists.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-semibold mb-2">
              {searchQuery ? "Aucune liste trouvée" : "Aucune liste de tâches"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery 
                ? "Essayez avec d'autres mots-clés" 
                : "Commencez par créer votre première liste"
              }
            </p>
            {!searchQuery && (
               <Button 
                variant="tasks"
                onClick={() => openModal('createTaskList')}
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer une liste
              </Button>
            )}
          </div>
        ) : (
          <DndContext 
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={filteredTaskLists.map(list => list.id)}
              strategy={verticalListSortingStrategy}
              disabled={!!searchQuery}
            >
              <div className="space-y-8">
                {filteredTaskLists.map((taskList) => (
                   <div key={taskList.id} className="relative">
                     <TaskListCard
                       taskList={taskList}
                       onCardClick={() => {
                         openModal('editTaskList', { id: taskList.id });
                       }}
                       onQuickAdd={() => {
                         window.scrollTo(0, 0);
                         navigate(`/create-task?listId=${taskList.id}`);
                       }}
                      onTaskCompleted={() => {
                        refreshTaskLists();
                      }}
                      filteredTasks={taskList.filteredTasks}
                      isDragDisabled={!!searchQuery}
                      isOverDropZone={overId === `droppable-${taskList.id}` || overId === taskList.id}
                     />
                    </div>
                 ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}